import { Alert, Box, Button, Checkbox, Flex, Heading, Text } from '@nrx/ui';
import { type JSX, useState } from 'react';

import { getApiBaseUrl } from '../../lib/api-base.js';
import { authFetch } from '../../lib/auth-fetch.js';
import { reportConsultError } from '../../lib/report-consult-error.js';

const WAIVE_FAILED_TEXT = "That didn't go through. Try again, or ask the member to add their card.";

/**
 * What a care manager sees in place of the card step on an FFS consult whose member owes a card on
 * file: the member should add it themselves, or the care manager can deliberately book without it.
 *
 * @remarks Booking without the card takes two steps on purpose: tick the acknowledgement, then press
 * the button. The button records the waiver (`POST /payment/waive`, audited against the care manager
 * who minted the link) and only then shows the booking frames. Nothing is saved for the member, so
 * the card is still required for their next booking.
 */
export function StaffCardRequiredNotice({
  jwt,
  onWaived,
}: {
  jwt: string;
  onWaived: () => void;
}): JSX.Element {
  const [acknowledged, setAcknowledged] = useState(false);
  const [state, setState] = useState<'idle' | 'saving' | 'failed'>('idle');

  const waive = async (): Promise<void> => {
    setState('saving');
    try {
      const res = await authFetch(jwt, `${getApiBaseUrl()}/payment/waive`, { method: 'POST' });
      if (!res.ok) throw new Error(`waive request failed (${res.status})`);
      onWaived();
    } catch (err) {
      reportConsultError('admin-fee-staff-waive', err);
      setState('failed');
    }
  };

  return (
    <Box className="consult-admin-fee-step">
      <Alert tone="warning">
        <Heading as="h3" size="4">
          The member needs to add a card first
        </Heading>
        <Text as="p">
          This member has a missed or late-cancelled consult, so they must put a card on file for
          administrative fees before booking. Staff can&apos;t enter card details. Ask the member to
          open the booking link we sent them and add their card there.
        </Text>
      </Alert>
      <Flex direction="column" gap="3" mt="4">
        {/* The input sits inside the label, which is what names it. */}
        <Flex as="label" align="center" gap="2">
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.currentTarget.checked)}
          />
          <Text>
            I&apos;m booking this consult without a card on file, and this will be recorded.
          </Text>
        </Flex>
        <Box>
          <Button
            tone="secondary"
            disabled={!acknowledged}
            busy={state === 'saving'}
            onClick={() => void waive()}
          >
            {state === 'saving' ? 'Recording…' : 'Book without a card'}
          </Button>
        </Box>
        {state === 'failed' ? <Alert tone="danger">{WAIVE_FAILED_TEXT}</Alert> : null}
      </Flex>
    </Box>
  );
}
